/**
 * Export services for hermit-μDC configurations
 */

import { DatacenterConfig } from '../types/configurator';
import { getServiceById } from '../data/services';

/**
 * Generate Ansible inventory in YAML format
 */
export function generateAnsibleInventory(config: DatacenterConfig): string {
  const lines: string[] = [
    '# Ansible inventory for hermit-μDC',
    `# Configuration: ${config.name}`,
    `# Generated: ${new Date().toISOString()}`,
    '---',
    'all:',
    '  vars:',
    `    hermit_domain: "${config.network.domain}"`,
    `    hermit_subnet: "${config.network.subnet}"`,
    `    hermit_gateway: "${config.network.gateway}"`,
    `    hermit_dns_server: "${config.network.gateway}"`,
    '',
    '    # VPN settings',
    `    vpn_enabled: ${config.security.vpnEnabled}`,
    `    vpn_port: ${config.security.vpnListenPort}`,
    `    vpn_subnet: "${config.security.vpnSubnet}"`,
    '',
    '    # Security settings',
    `    encrypt_storage: ${config.security.encryptStorage}`,
    `    audit_logging: ${config.security.auditLogging}`,
    `    firewall_default_deny: ${config.security.firewallDefaultDeny}`,
    '',
    '  children:',
  ];

  // Group nodes by type
  const nodesByType: Record<string, typeof config.nodes> = {};
  for (const node of config.nodes) {
    if (!nodesByType[node.type]) {
      nodesByType[node.type] = [];
    }
    nodesByType[node.type].push(node);
  }

  for (const [type, nodes] of Object.entries(nodesByType)) {
    lines.push(`    ${type}:`);
    lines.push('      hosts:');
    for (const node of nodes) {
      lines.push(`        ${node.hostname}:`);
      lines.push(`          ansible_host: ${node.ip}`);
      lines.push(`          cpu_cores: ${node.specs.cpuCores}`);
      lines.push(`          memory_gb: ${node.specs.memoryGB}`);
      lines.push(`          storage_gb: ${node.specs.storageGB}`);
      if (node.role) {
        lines.push(`          node_role: ${node.role}`);
      }

      // Add services for this node
      const nodeServices = config.serviceAllocations.filter(
        (a) => a.nodeId === node.id && a.enabled
      );
      if (nodeServices.length > 0) {
        lines.push('          services:');
        for (const alloc of nodeServices) {
          const svc = getServiceById(alloc.serviceId);
          if (svc) {
            lines.push(`            - name: ${svc.id}`);
            lines.push(`              port: ${svc.port}`);
          }
        }
      }
    }
  }

  return lines.join('\n');
}

/**
 * Generate Bill of Materials as CSV
 */
export function generateBOM(config: DatacenterConfig): string {
  const lines: string[] = [
    'Qty,Type,Hostname,Model,CPU Cores,Memory (GB),Storage (GB),Power (W),Unit Cost,Total Cost',
  ];

  for (const node of config.nodes) {
    const unitCost = calculateNodeCost(node);
    lines.push(
      [
        1,
        node.type,
        node.hostname,
        `"${node.specs.model || 'Generic'}"`,
        node.specs.cpuCores,
        node.specs.memoryGB,
        node.specs.storageGB,
        node.specs.powerWatts,
        unitCost.toFixed(2),
        unitCost.toFixed(2),
      ].join(',')
    );
  }

  // Network equipment
  lines.push('1,Network,switch01,8-port Gigabit Switch,0,0,0,5,25.00,25.00');

  // Power
  const totalPower = config.estimatedPower;
  if (totalPower > 100) {
    lines.push('1,Power,pdu01,Power Distribution Unit,0,0,0,0,50.00,50.00');
  }

  // Summary
  lines.push('');
  lines.push(`# Summary`);
  lines.push(`Total Nodes,${config.nodes.length}`);
  lines.push(`Total Power (W),${config.estimatedPower}`);
  lines.push(`Estimated Total Cost,$${config.estimatedCost.toFixed(2)}`);

  return lines.join('\n');
}

/**
 * Generate WireGuard server configuration
 */
export function generateWireGuardConfig(config: DatacenterConfig): string {
  if (!config.security.vpnEnabled) {
    return '# VPN is disabled in this configuration';
  }

  const lines: string[] = [
    '# WireGuard server configuration for hermit-μDC',
    `# Generated: ${new Date().toISOString()}`,
    '',
    '[Interface]',
    `Address = ${config.security.vpnSubnet.replace('/24', '/1')}`,
    `ListenPort = ${config.security.vpnListenPort}`,
    'PrivateKey = <GENERATE_WITH_wg_genkey>',
    '',
    '# Enable IP forwarding (run: sysctl -w net.ipv4.ip_forward=1)',
    '',
    'PostUp = iptables -A FORWARD -i %i -j ACCEPT; iptables -A FORWARD -o %i -j ACCEPT',
    'PostDown = iptables -D FORWARD -i %i -j ACCEPT; iptables -D FORWARD -o %i -j ACCEPT',
    '',
  ];

  for (const peer of config.security.peers) {
    if (!peer.enabled) continue;

    lines.push(`# Peer: ${peer.name}`);
    lines.push('[Peer]');
    if (peer.publicKey) {
      lines.push(`PublicKey = ${peer.publicKey}`);
    } else {
      lines.push('PublicKey = <PEER_PUBLIC_KEY>');
    }
    lines.push(`AllowedIPs = ${peer.allowedIPs}`);
    if (peer.description) {
      lines.push(`# ${peer.description}`);
    }
    lines.push('');
  }

  if (config.security.peers.filter((p) => p.enabled).length === 0) {
    lines.push('# No peers configured');
    lines.push('# Add peers with:');
    lines.push('#   wg set wg0 peer <PUBLIC_KEY> allowed-ips 10.200.0.2/32');
  }

  return lines.join('\n');
}

/**
 * Generate Docker Compose file for services
 */
export function generateDockerCompose(config: DatacenterConfig): string {
  const gateway = config.nodes.find((n) => n.type === 'gateway');
  if (!gateway) {
    return '# No gateway configured';
  }

  const gatewayServices = config.serviceAllocations.filter(
    (a) => a.nodeId === gateway.id && a.enabled
  );

  const lines: string[] = [
    '# Docker Compose for hermit-μDC services',
    `# Gateway: ${gateway.hostname}`,
    `# Generated: ${new Date().toISOString()}`,
    '',
    'version: "3.8"',
    '',
    'services:',
  ];

  for (const alloc of gatewayServices) {
    const svc = getServiceById(alloc.serviceId);
    if (!svc) continue;

    switch (svc.id) {
      case 'registry':
        lines.push('  registry:');
        lines.push('    image: registry:2');
        lines.push('    container_name: hermit-registry');
        lines.push('    restart: unless-stopped');
        lines.push('    ports:');
        lines.push('      - "5000:5000"');
        lines.push('    volumes:');
        lines.push('      - registry-data:/var/lib/registry');
        lines.push('');
        break;

      case 'minio':
        lines.push('  minio:');
        lines.push('    image: minio/minio:latest');
        lines.push('    container_name: hermit-minio');
        lines.push('    restart: unless-stopped');
        lines.push('    command: server /data --console-address ":9001"');
        lines.push('    ports:');
        lines.push('      - "9000:9000"');
        lines.push('      - "9001:9001"');
        lines.push('    volumes:');
        lines.push('      - minio-data:/data');
        lines.push('    environment:');
        lines.push('      MINIO_ROOT_USER: admin');
        lines.push('      MINIO_ROOT_PASSWORD: changeme123');
        lines.push('');
        break;

      case 'prometheus':
        lines.push('  prometheus:');
        lines.push('    image: prom/prometheus:latest');
        lines.push('    container_name: hermit-prometheus');
        lines.push('    restart: unless-stopped');
        lines.push('    ports:');
        lines.push('      - "9090:9090"');
        lines.push('    volumes:');
        lines.push('      - ./prometheus.yml:/etc/prometheus/prometheus.yml');
        lines.push('      - prometheus-data:/prometheus');
        lines.push('');
        break;

      case 'grafana':
        lines.push('  grafana:');
        lines.push('    image: grafana/grafana:latest');
        lines.push('    container_name: hermit-grafana');
        lines.push('    restart: unless-stopped');
        lines.push('    ports:');
        lines.push('      - "3000:3000"');
        lines.push('    volumes:');
        lines.push('      - grafana-data:/var/lib/grafana');
        lines.push('    environment:');
        lines.push('      GF_SECURITY_ADMIN_PASSWORD: changeme');
        lines.push('');
        break;

      case 'portainer':
        lines.push('  portainer:');
        lines.push('    image: portainer/portainer-ce:latest');
        lines.push('    container_name: hermit-portainer');
        lines.push('    restart: unless-stopped');
        lines.push('    ports:');
        lines.push('      - "9443:9443"');
        lines.push('    volumes:');
        lines.push('      - /var/run/docker.sock:/var/run/docker.sock');
        lines.push('      - portainer-data:/data');
        lines.push('');
        break;
    }
  }

  lines.push('volumes:');
  lines.push('  registry-data:');
  lines.push('  minio-data:');
  lines.push('  prometheus-data:');
  lines.push('  grafana-data:');
  lines.push('  portainer-data:');

  return lines.join('\n');
}

/**
 * Generate network diagram in SVG format
 */
export function generateNetworkDiagramSVG(config: DatacenterConfig): string {
  const width = 800;
  const height = 500;
  const gateway = config.nodes.find((n) => n.type === 'gateway');

  const nodeColors: Record<string, string> = {
    gateway: '#f97316',
    compute: '#3b82f6',
    storage: '#22c55e',
    daq: '#a855f7',
  };

  let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">
  <defs>
    <style>
      .node-label { font-family: monospace; font-size: 12px; }
      .node-ip { font-family: monospace; font-size: 10px; fill: #666; }
      .title { font-family: sans-serif; font-size: 16px; font-weight: bold; }
      .subtitle { font-family: sans-serif; font-size: 11px; fill: #666; }
    </style>
  </defs>

  <!-- Background -->
  <rect width="${width}" height="${height}" fill="#f8fafc"/>

  <!-- Title -->
  <text x="20" y="30" class="title">${config.name}</text>
  <text x="20" y="48" class="subtitle">${config.network.subnet} | ${config.nodes.length} nodes | ${config.estimatedPower}W</text>

  <!-- Network backbone -->
  <line x1="100" y1="150" x2="700" y2="150" stroke="#e2e8f0" stroke-width="4"/>
  `;

  // Position nodes
  const nodePositions: { node: typeof config.nodes[0]; x: number; y: number }[] = [];

  if (gateway) {
    nodePositions.push({ node: gateway, x: 400, y: 100 });
  }

  const otherNodes = config.nodes.filter((n) => n.type !== 'gateway');
  const spacing = Math.min(150, (width - 200) / Math.max(otherNodes.length, 1));
  const startX = (width - (otherNodes.length - 1) * spacing) / 2;

  otherNodes.forEach((node, i) => {
    nodePositions.push({ node, x: startX + i * spacing, y: 250 });
  });

  // Draw connections
  if (gateway) {
    const gwPos = nodePositions.find((p) => p.node.id === gateway.id)!;
    for (const pos of nodePositions) {
      if (pos.node.id === gateway.id) continue;
      svg += `  <line x1="${gwPos.x}" y1="${gwPos.y + 30}" x2="${pos.x}" y2="${pos.y - 30}"
    stroke="${nodeColors[pos.node.type]}" stroke-width="2" stroke-dasharray="5,3"/>
`;
    }
  }

  // Draw nodes
  for (const { node, x, y } of nodePositions) {
    const color = nodeColors[node.type] || '#64748b';
    svg += `
  <!-- Node: ${node.hostname} -->
  <g transform="translate(${x - 40}, ${y - 25})">
    <rect width="80" height="50" rx="8" fill="${color}" opacity="0.1" stroke="${color}" stroke-width="2"/>
    <text x="40" y="22" text-anchor="middle" class="node-label" fill="${color}">${node.hostname}</text>
    <text x="40" y="38" text-anchor="middle" class="node-ip">${node.ip}</text>
  </g>`;
  }

  // Legend
  svg += `
  <!-- Legend -->
  <g transform="translate(20, ${height - 80})">
    <text x="0" y="0" class="subtitle">Node Types:</text>`;

  let legendX = 0;
  for (const [type, color] of Object.entries(nodeColors)) {
    const count = config.nodes.filter((n) => n.type === type).length;
    if (count === 0) continue;
    svg += `
    <rect x="${legendX}" y="10" width="12" height="12" rx="2" fill="${color}"/>
    <text x="${legendX + 18}" y="20" class="node-ip">${type} (${count})</text>`;
    legendX += 100;
  }

  svg += `
  </g>
</svg>`;

  return svg;
}

/**
 * Generate PlantUML deployment diagram
 */
export function generatePlantUML(config: DatacenterConfig): string {
  const lines: string[] = [
    '@startuml',
    `title ${config.name}`,
    `caption ${config.network.subnet} | ${config.nodes.length} nodes | ${config.estimatedPower}W`,
    '',
    '!define ICONURL https://raw.githubusercontent.com/tupadr3/plantuml-icon-font-sprites/v2.4.0',
    '!include ICONURL/font-awesome-5/server.puml',
    '!include ICONURL/font-awesome-5/database.puml',
    '!include ICONURL/font-awesome-5/network_wired.puml',
    '',
    'skinparam backgroundColor #f8fafc',
    'skinparam node {',
    '    BackgroundColor white',
    '    BorderColor #64748b',
    '}',
    '',
  ];

  // Define the hermit datacenter
  lines.push(`cloud "${config.name}" as hermit {`);

  // Network frame
  lines.push(`    frame "${config.network.subnet}" as network {`);

  // Gateway nodes
  const gateways = config.nodes.filter((n) => n.type === 'gateway');
  for (const gw of gateways) {
    lines.push(`        node "${gw.hostname}\\n${gw.ip}" as ${gw.id.replace(/-/g, '_')} #f97316`);
  }

  // Compute nodes
  const computes = config.nodes.filter((n) => n.type === 'compute');
  if (computes.length > 0) {
    lines.push('');
    lines.push('        package "Compute Nodes" as compute_pkg #dbeafe {');
    for (const node of computes) {
      const role = node.role ? `\\n[${node.role}]` : '';
      lines.push(`            node "${node.hostname}\\n${node.ip}${role}" as ${node.id.replace(/-/g, '_')} #3b82f6`);
    }
    lines.push('        }');
  }

  // Storage nodes
  const storages = config.nodes.filter((n) => n.type === 'storage');
  if (storages.length > 0) {
    lines.push('');
    lines.push('        package "Storage Nodes" as storage_pkg #dcfce7 {');
    for (const node of storages) {
      lines.push(`            database "${node.hostname}\\n${node.ip}" as ${node.id.replace(/-/g, '_')} #22c55e`);
    }
    lines.push('        }');
  }

  // DAQ nodes
  const daqs = config.nodes.filter((n) => n.type === 'daq');
  if (daqs.length > 0) {
    lines.push('');
    lines.push('        package "DAQ Controllers" as daq_pkg #f3e8ff {');
    for (const node of daqs) {
      lines.push(`            node "${node.hostname}\\n${node.ip}" as ${node.id.replace(/-/g, '_')} #a855f7`);
    }
    lines.push('        }');
  }

  lines.push('    }');
  lines.push('}');
  lines.push('');

  // VPN connection
  if (config.security.vpnEnabled) {
    lines.push('actor "Operator" as operator');
    lines.push(`cloud "WireGuard VPN\\n:${config.security.vpnListenPort}" as vpn #22c55e`);
    lines.push('operator --> vpn');
    if (gateways.length > 0) {
      lines.push(`vpn --> ${gateways[0].id.replace(/-/g, '_')}`);
    }
    lines.push('');
  }

  // Connections from gateway to other nodes
  if (gateways.length > 0) {
    const gwId = gateways[0].id.replace(/-/g, '_');
    for (const node of config.nodes) {
      if (node.type !== 'gateway') {
        lines.push(`${gwId} --> ${node.id.replace(/-/g, '_')}`);
      }
    }
  }

  lines.push('');
  lines.push('legend right');
  lines.push(`    | Type | Count | Power |`);
  lines.push('    |------|-------|-------|');
  lines.push(`    | Gateway | ${gateways.length} | ${gateways.reduce((s, n) => s + n.specs.powerWatts, 0)}W |`);
  lines.push(`    | Compute | ${computes.length} | ${computes.reduce((s, n) => s + n.specs.powerWatts, 0)}W |`);
  lines.push(`    | Storage | ${storages.length} | ${storages.reduce((s, n) => s + n.specs.powerWatts, 0)}W |`);
  lines.push(`    | DAQ | ${daqs.length} | ${daqs.reduce((s, n) => s + n.specs.powerWatts, 0)}W |`);
  lines.push('endlegend');
  lines.push('');
  lines.push('@enduml');

  return lines.join('\n');
}

/**
 * Generate Mermaid diagram for documentation (simple, works in GitHub/GitLab markdown)
 */
export function generateMermaidDiagram(config: DatacenterConfig): string {
  const lines: string[] = [
    '```mermaid',
    'graph TD',
    `    subgraph hermit["${config.name}"]`,
    `        subgraph network["${config.network.subnet}"]`,
  ];

  // Group nodes by type
  const gateway = config.nodes.find((n) => n.type === 'gateway');
  const computeNodes = config.nodes.filter((n) => n.type === 'compute');
  const storageNodes = config.nodes.filter((n) => n.type === 'storage');
  const daqNodes = config.nodes.filter((n) => n.type === 'daq');

  if (gateway) {
    lines.push(`            GW[("${gateway.hostname}<br/>${gateway.ip}<br/>Gateway")]`);
  }

  if (computeNodes.length > 0) {
    lines.push('            subgraph compute["Compute"]');
    for (const node of computeNodes) {
      const role = node.role ? `<br/>${node.role}` : '';
      lines.push(`                ${node.id.replace(/-/g, '_')}["${node.hostname}<br/>${node.ip}${role}"]`);
    }
    lines.push('            end');
  }

  if (storageNodes.length > 0) {
    lines.push('            subgraph storage["Storage"]');
    for (const node of storageNodes) {
      lines.push(`                ${node.id.replace(/-/g, '_')}[("${node.hostname}<br/>${node.ip}")]`);
    }
    lines.push('            end');
  }

  if (daqNodes.length > 0) {
    lines.push('            subgraph daq["DAQ"]');
    for (const node of daqNodes) {
      lines.push(`                ${node.id.replace(/-/g, '_')}["${node.hostname}<br/>${node.ip}"]`);
    }
    lines.push('            end');
  }

  // Add connections
  if (gateway) {
    for (const node of config.nodes) {
      if (node.id !== gateway.id) {
        lines.push(`            GW --> ${node.id.replace(/-/g, '_')}`);
      }
    }
  }

  lines.push('        end');
  lines.push('    end');

  // Add external VPN connection
  if (config.security.vpnEnabled) {
    lines.push('    VPN(("VPN<br/>WireGuard")) --> GW');
    lines.push('    style VPN fill:#22c55e,stroke:#16a34a,color:#fff');
  }

  // Add styling
  lines.push('');
  lines.push('    style GW fill:#f97316,stroke:#ea580c,color:#fff');
  lines.push('    style network fill:#f1f5f9,stroke:#94a3b8');
  if (computeNodes.length > 0) lines.push('    style compute fill:#dbeafe,stroke:#3b82f6');
  if (storageNodes.length > 0) lines.push('    style storage fill:#dcfce7,stroke:#22c55e');
  if (daqNodes.length > 0) lines.push('    style daq fill:#f3e8ff,stroke:#a855f7');

  lines.push('```');

  return lines.join('\n');
}

/**
 * Generate bootstrap shell script
 */
export function generateBootstrapScript(config: DatacenterConfig): string {
  const lines: string[] = [
    '#!/bin/bash',
    '#',
    `# hermit-μDC Bootstrap Script`,
    `# Configuration: ${config.name}`,
    `# Generated: ${new Date().toISOString()}`,
    '#',
    '# Usage: sudo ./bootstrap.sh <node-hostname>',
    '#',
    'set -euo pipefail',
    '',
    '# Configuration',
    `DOMAIN="${config.network.domain}"`,
    `SUBNET="${config.network.subnet}"`,
    `GATEWAY="${config.network.gateway}"`,
    `DNS_SERVER="${config.network.gateway}"`,
    '',
    '# Node definitions',
    'declare -A NODES=(',
  ];

  for (const node of config.nodes) {
    lines.push(`    ["${node.hostname}"]="${node.ip}"`);
  }
  lines.push(')');
  lines.push('');

  lines.push('declare -A NODE_TYPES=(');
  for (const node of config.nodes) {
    lines.push(`    ["${node.hostname}"]="${node.type}"`);
  }
  lines.push(')');
  lines.push('');

  lines.push(`# Check arguments
if [ "\${1:-}" = "" ]; then
    echo "Usage: \$0 <hostname>"
    echo "Available nodes:"
    for node in "\${!NODES[@]}"; do
        echo "  - \$node (\${NODES[\$node]}) - \${NODE_TYPES[\$node]}"
    done
    exit 1
fi

HOSTNAME="\$1"
if [ -z "\${NODES[\$HOSTNAME]:-}" ]; then
    echo "Error: Unknown node '\$HOSTNAME'"
    exit 1
fi

IP="\${NODES[\$HOSTNAME]}"
TYPE="\${NODE_TYPES[\$HOSTNAME]}"

echo "=== hermit-μDC Bootstrap ==="
echo "Node:     \$HOSTNAME"
echo "IP:       \$IP"
echo "Type:     \$TYPE"
echo "Domain:   \$DOMAIN"
echo ""

# Set hostname
echo "Setting hostname..."
hostnamectl set-hostname "\$HOSTNAME.\$DOMAIN"

# Configure static IP (netplan example)
echo "Configuring network..."
cat > /etc/netplan/99-hermit.yaml << EOF
network:
  version: 2
  ethernets:
    eth0:
      addresses:
        - \$IP/24
      routes:
        - to: \$SUBNET
          via: \$GATEWAY
      nameservers:
        addresses:
          - \$DNS_SERVER
        search:
          - \$DOMAIN
EOF
`);

  lines.push(`# Apply network config
netplan apply || echo "Note: netplan not available, configure manually"

# Update /etc/hosts
echo "Updating /etc/hosts..."
cat >> /etc/hosts << EOF

# hermit-μDC nodes`);

  for (const node of config.nodes) {
    lines.push(`echo "${node.ip}    ${node.hostname}.${config.network.domain} ${node.hostname}" >> /etc/hosts`);
  }

  lines.push(`
EOF

# Install base packages
echo "Installing base packages..."
apt-get update
apt-get install -y \\
    docker.io \\
    docker-compose \\
    curl \\
    htop \\
    iotop \\
    net-tools

# Enable Docker
systemctl enable docker
systemctl start docker

# Node-specific setup
case "\$TYPE" in
    gateway)
        echo "Configuring gateway services..."
        # Install dnsmasq for DHCP/DNS
        apt-get install -y dnsmasq

        # Enable IP forwarding
        echo "net.ipv4.ip_forward=1" >> /etc/sysctl.conf
        sysctl -p
        `);

  if (config.security.vpnEnabled) {
    lines.push(`
        # Install WireGuard
        apt-get install -y wireguard
        echo "WireGuard installed. Configure with: wg genkey | tee /etc/wireguard/private.key | wg pubkey > /etc/wireguard/public.key"
        `);
  }

  lines.push(`
        ;;
    compute)
        echo "Configuring compute node..."
        # Add to docker swarm or configure standalone
        ;;
    storage)
        echo "Configuring storage node..."
        # Install MinIO or prepare storage
        mkdir -p /data/minio
        ;;
    daq)
        echo "Configuring DAQ node..."
        # Install data acquisition packages
        apt-get install -y python3-pip
        pip3 install pymodbus
        ;;
esac

echo ""
echo "=== Bootstrap Complete ==="
echo "Reboot recommended: sudo reboot"
`);

  return lines.join('\n');
}

/**
 * Generate markdown documentation
 */
export function generateMarkdownDoc(config: DatacenterConfig): string {
  const lines: string[] = [
    `# ${config.name}`,
    '',
    config.description || '_No description provided._',
    '',
    '## Overview',
    '',
    '| Property | Value |',
    '|----------|-------|',
    `| Scenario | ${config.scenario} |`,
    `| Nodes | ${config.nodes.length} |`,
    `| Estimated Power | ${config.estimatedPower}W |`,
    `| Estimated Cost | $${config.estimatedCost.toFixed(0)} |`,
    `| Domain | ${config.network.domain} |`,
    `| Subnet | ${config.network.subnet} |`,
    '',
    '## Network Topology',
    '',
    generateMermaidDiagram(config),
    '',
    '## Nodes',
    '',
    '| Hostname | Type | IP Address | CPU | RAM | Storage | Power |',
    '|----------|------|------------|-----|-----|---------|-------|',
  ];

  for (const node of config.nodes) {
    const role = node.role ? ` (${node.role})` : '';
    lines.push(
      `| ${node.hostname} | ${node.type}${role} | ${node.ip} | ${node.specs.cpuCores} cores | ${node.specs.memoryGB}GB | ${node.specs.storageGB}GB | ${node.specs.powerWatts}W |`
    );
  }

  lines.push('');
  lines.push('## Services');
  lines.push('');

  // Group services by node
  const servicesByNode: Record<string, string[]> = {};
  for (const alloc of config.serviceAllocations.filter((a) => a.enabled)) {
    const node = config.nodes.find((n) => n.id === alloc.nodeId);
    if (!node) continue;
    if (!servicesByNode[node.hostname]) {
      servicesByNode[node.hostname] = [];
    }
    const svc = getServiceById(alloc.serviceId);
    if (svc) {
      servicesByNode[node.hostname].push(`${svc.name} (:${svc.port})`);
    }
  }

  if (Object.keys(servicesByNode).length > 0) {
    for (const [hostname, services] of Object.entries(servicesByNode)) {
      lines.push(`### ${hostname}`);
      for (const svc of services) {
        lines.push(`- ${svc}`);
      }
      lines.push('');
    }
  } else {
    lines.push('_No services allocated._');
    lines.push('');
  }

  lines.push('## Security');
  lines.push('');
  lines.push('| Setting | Status |');
  lines.push('|---------|--------|');
  lines.push(`| VPN | ${config.security.vpnEnabled ? `Enabled (port ${config.security.vpnListenPort})` : 'Disabled'} |`);
  lines.push(`| Storage Encryption | ${config.security.encryptStorage ? 'LUKS Enabled' : 'Disabled'} |`);
  lines.push(`| Audit Logging | ${config.security.auditLogging ? 'Enabled' : 'Disabled'} |`);
  lines.push(`| Firewall | ${config.security.firewallDefaultDeny ? 'Default Deny' : 'Permissive'} |`);
  lines.push('');

  if (config.security.vpnEnabled && config.security.peers.length > 0) {
    lines.push('### VPN Peers');
    lines.push('');
    lines.push('| Name | Allowed IPs | Status |');
    lines.push('|------|-------------|--------|');
    for (const peer of config.security.peers) {
      const status = peer.enabled ? 'Active' : 'Disabled';
      lines.push(`| ${peer.name} | ${peer.allowedIPs} | ${status} |`);
    }
    lines.push('');
  }

  lines.push('---');
  lines.push(`_Generated by hermit-μDC Configurator on ${new Date().toISOString()}_`);

  return lines.join('\n');
}

function calculateNodeCost(node: DatacenterConfig['nodes'][0]): number {
  const baseCost =
    node.type === 'gateway' ? 50 :
    node.type === 'storage' ? 200 :
    node.type === 'daq' ? 150 :
    100;
  const specsCost =
    node.specs.cpuCores * 20 +
    node.specs.memoryGB * 5 +
    node.specs.storageGB * 0.5;
  return baseCost + specsCost;
}
